import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaCakeCandles, FaUsers } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import { Event, EventType, EventViewType } from '../../../types/event';
import useEvents from '../../../hooks/community/useEvents';
import eventService from '../../../services/community/eventService';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { formatDate } from '../../../utils/stringUtils';
import { RootState } from '../../../redux/reducers';
import { CommunityInvitePropertyType } from '../../../models/inviteService.model';
import { BasicInfo } from '../../../types/BasicInfo';
import useLanguage from '../../../hooks/account/useLanguage';
import Language from '../../../types/language';
import ModalLayout from '../../components/layouts/ModalLayout';
import Button from '../../components/buttons/Button';
import ProfilePicture from '../../components/misc/ProfilePicture';

const EVENT_COLORS = {
  [EventType.WORK]: 'bg-blue-500',
  [EventType.SOCIAL]: 'bg-green-500',
  [EventType.SPORT]: 'bg-orange-500',
  [EventType.OTHER]: 'bg-purple-500',
  'DEFAULT': 'bg-purple-500',
};

interface MonthEvent extends Event {
  dateKey: string;
}

type ShiftAssignee = {
  key: string;
  firstName: string;
  lastName: string;
  picture?: string;
};

const SHIFT_CARD_STYLE = 'bg-primary-300 bg-opacity-40 text-primary-900';

const isShiftEvent = (event: Event): boolean => Boolean(event.scheduleId);

function getShiftAssignees(shift: Event): ShiftAssignee[] {
  const participants: ShiftAssignee[] = shift.participants.map((participant) => ({
    key: `member-${participant.id}`,
    firstName: participant.firstName,
    lastName: participant.lastName,
    picture: participant.picture,
  }));

  const externalInvites: ShiftAssignee[] = shift.externalInvites.map((invite) => ({
    key: `external-${invite.externalInviteId}`,
    firstName: invite.firstName,
    lastName: invite.lastName,
    picture: invite.picture,
  }));

  return [...participants, ...externalInvites];
}

function getAssigneeAlias(assignee: ShiftAssignee): string {
  return `${assignee.firstName} ${assignee.lastName}`.trim();
}

export default function EventsAgendaTab(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const community = useSelectedCommunity();
  const allEvents = useEvents(EventViewType.ALL);
  const connections = useSelector((state: RootState) => state.connections);
  const currentLanguage = useLanguage();
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Event | null>(null);

  const localizedCurrentMonth = useMemo(() => {
    const locale = currentLanguage === Language.NL ? 'nl' : 'en';
    return currentMonth.locale(locale);
  }, [currentMonth, currentLanguage]);

  useEffect(() => {
    eventService.getUpcomingEvents().then(() => eventService.prependPastEvents());
  }, [community]);

  const isBirthdayRequested = useMemo(() => {
    if (!community?.inviteFields) return false;
    
    const requestedFields = [
      ...community.inviteFields.mandatoryFields,
      ...community.inviteFields.optionalFields,
    ];
    
    return requestedFields.includes(CommunityInvitePropertyType.BIRTHDATE);
  }, [community?.inviteFields]);

  const getBirthdaysForDate = useMemo(() => {
    if (!isBirthdayRequested) return () => [];

    const birthdaysByDate: Record<string, Array<{ name: string; age: number }>> = {};

    connections
      .filter((connection) => connection.fields[BasicInfo.BIRTHDATE])
      .forEach((connection) => {
        const birthDateString = connection.fields[BasicInfo.BIRTHDATE];
        const [day, month] = birthDateString.split('-').map(Number);
        
        if (!Number.isNaN(month) && !Number.isNaN(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          const dateKey = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          
          if (!birthdaysByDate[dateKey]) {
            birthdaysByDate[dateKey] = [];
          }
          
          const [, , year] = birthDateString.split('-').map(Number);
          const currentYear = dayjs().year();
          const age = currentYear - year;
          
          birthdaysByDate[dateKey].push({
            name: `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`,
            age
          });
        }
      });

    return (date: dayjs.Dayjs) => {
      const dateKey = date.format('MM-DD');
      return birthdaysByDate[dateKey] || [];
    };
  }, [connections, isBirthdayRequested]);

  const daysOfWeek = [
    t('general.daysOfWeek.monday'),
    t('general.daysOfWeek.tuesday'),
    t('general.daysOfWeek.wednesday'),
    t('general.daysOfWeek.thursday'),
    t('general.daysOfWeek.friday'),
    t('general.daysOfWeek.saturday'),
    t('general.daysOfWeek.sunday'),
  ];

  const calendarWeeks = useMemo(() => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    
    let startDate = startOfMonth;
    while (startDate.day() !== 1) {
      startDate = startDate.subtract(1, 'day');
    }
    
    let endDate = endOfMonth;
    while (endDate.day() !== 0) {
      endDate = endDate.add(1, 'day');
    }

    const weeks = [];
    let currentWeek = startDate;

    while (currentWeek.isBefore(endDate, 'day') || currentWeek.isSame(endDate, 'day')) {
      const daysInWeek = [];
      for (let i = 0; i < 7; i += 1) {
        daysInWeek.push(currentWeek.add(i, 'day'));
      }
      weeks.push(daysInWeek);
      currentWeek = currentWeek.add(1, 'week');
    }

    return weeks;
  }, [currentMonth]);

  const eventsByDate = useMemo(() => {
    let monthStart = currentMonth.startOf('month');
    while (monthStart.day() !== 1) {
      monthStart = monthStart.subtract(1, 'day');
    }
    
    let monthEnd = currentMonth.endOf('month');
    while (monthEnd.day() !== 0) {
      monthEnd = monthEnd.add(1, 'day');
    }

    const eventsInRange = allEvents.filter(event => {
      const eventDate = dayjs(event.startTime);
      return eventDate.isAfter(monthStart, 'day') || eventDate.isSame(monthStart, 'day') && 
             eventDate.isBefore(monthEnd, 'day') || eventDate.isSame(monthEnd, 'day');
    });

    const grouped: Record<string, MonthEvent[]> = {};
    eventsInRange.forEach(event => {
      const dateKey = dayjs(event.startTime).format('YYYY-MM-DD');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push({
        ...event,
        dateKey
      });
    });

    return grouped;
  }, [allEvents, currentMonth]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => direction === 'prev' ? prev.subtract(1, 'month') : prev.add(1, 'month'));
  };

  const navigateToEvent = (eventId: number) => {
    navigate('/events/details', { state: { eventId, origin: 'calendar' } });
  };

  const closeShiftModal = () => {
    setShiftModalOpen(false);
    setSelectedShift(null);
  };

  const openShiftModal = (shift: Event) => {
    setSelectedShift(shift);
    setShiftModalOpen(true);
  };

  const handleCalendarItemClick = (event: Event) => {
    if (isShiftEvent(event)) {
      openShiftModal(event);
      return;
    }

    navigateToEvent(event.id);
  };

  const isToday = (date: dayjs.Dayjs) => date.isSame(dayjs(), 'day');
  const isCurrentMonth = (date: dayjs.Dayjs) => date.isSame(currentMonth, 'month');

  return (
    <div className="flex flex-1 flex-col gap-5 pb-4">
      {/* Header with navigation */}
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-4">
          <div className="flex items-center w-full gap-2">
            <button
              type='button'
              onClick={() => navigateMonth('prev')}
              className="flex items-center justify-center rounded-[8px] bg-primary-900 p-2 text-secondary-200 transition-colors hover:bg-opacity-75"
            >
              <FaChevronLeft className="w-4 h-4" />
            </button>
            <h2 className="text-2xl font-semibold text-primary-900">
              {localizedCurrentMonth.format('MMMM YYYY')}
            </h2>
            <button
              type='button'
              onClick={() => navigateMonth('next')}
              className="flex items-center justify-center rounded-[8px] bg-primary-900 p-2 text-secondary-200 transition-colors hover:bg-opacity-75"
            >
              <FaChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 bg-secondary-50 bg-opacity-5 rounded-[12px] border border-secondary-200 border-opacity-50 overflow-hidden">
        {/* Days Header */}
        <div className="grid grid-cols-7 border-b border-secondary-200">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="p-4 text-center bg-secondary-200 bg-opacity-50 border-l border-secondary-200 first:border-l-0"
            >
              <div className="text-sm font-semibold text-primary-900">
                {day}
              </div>
            </div>
          ))}
        </div>

        {/* Calendar Weeks */}
        <div className="overflow-auto">
          {calendarWeeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 border-b border-secondary-200 border-opacity-30 min-h-[90px]">
              {week.map((day) => {
                const dateKey = day.format('YYYY-MM-DD');
                const eventsForDay = eventsByDate[dateKey] || [];
                const birthdaysForDay = getBirthdaysForDate(day);
                const hasBirthdays = birthdaysForDay.length > 0;
                
                return (
                  <div
                    key={day.format('YYYY-MM-DD')}
                    className={`border-l border-secondary-200 border-opacity-30 first:border-l-0 p-3 relative transition-all duration-500 ${
                      isToday(day) ? 'bg-primary bg-opacity-5' : ''
                    } ${
                      !isCurrentMonth(day) ? 'text-gray-400 bg-secondary-200 bg-opacity-30' : ''
                    } ${
                      hasBirthdays ? 'bg-gradient-to-br from-pink-100 via-purple-50 to-yellow-50 border-2 border-pink-300 shadow-lg shadow-pink-200/50' : ''
                    }`}
                  >
                    {/* Birthday celebration effect */}
                    {hasBirthdays && (
                      <>
                        <div className="absolute inset-0 rounded-sm bg-gradient-to-r from-pink-400 via-purple-400 to-yellow-400 opacity-20" />
                        <div className="absolute top-1 left-1 z-20">
                          <FaCakeCandles 
                            className="w-5 h-5 mt-1 text-pink-600 animate-bounce drop-shadow-lg"
                            style={{ animationDuration: '1s' }}
                          />
                        </div>
                      </>
                    )}

                    {/* Day number */}
                    <div className={`text-right mb-3 text-sm relative z-10 ${
                      isToday(day) ? 'bg-primary text-secondary-50 w-6 h-6 rounded-full flex items-center justify-center ml-auto font-semibold' : 
                      hasBirthdays ? 'text-pink-800 font-bold bg-gradient-to-r from-pink-200 to-purple-200 w-7 h-7 rounded-full flex items-center justify-center ml-auto border-2 border-pink-400 shadow-md' : 'text-primary-900'
                    }`}>
                      {day.format('D')}
                    </div>
                    
                    {/* Birthday indicator */}
                    {hasBirthdays && (
                      <div className="mb-2 relative z-10">
                        <div className="flex text-xs text-pink-700 font-bold bg-gradient-to-r from-pink-200 via-purple-200 to-yellow-200 rounded-[6px] px-2 py-1 truncate border border-pink-300 shadow-sm">
                          <FaCakeCandles className='mr-1' /> {birthdaysForDay.map(b => b.name.split(' ')[0]).join(', ')}
                        </div>
                      </div>
                    )}
                    
                    {/* Events for this day */}
                    <div className="space-y-1">
                      {eventsForDay.slice(0, hasBirthdays ? 3 : 4).map((event) => {
                        const shift = isShiftEvent(event);
                        const assignees = shift ? getShiftAssignees(event) : [];
                        const visibleAssignees = assignees.slice(0, 2);
                        const hiddenAssigneesCount = assignees.length - visibleAssignees.length;

                        return (
                          <div
                            key={event.id}
                            className={`rounded-[6px] p-1 cursor-pointer text-xs truncate ${
                              shift ? SHIFT_CARD_STYLE : `${EVENT_COLORS[event.type]} text-white`
                            }`}
                            onClick={() => handleCalendarItemClick(event)}
                            title={`${event.title} - ${formatDate(event.startTime)}`}
                          >
                            <div className="font-medium truncate">{event.title}</div>
                            <div className={`text-xs truncate ${shift ? 'text-primary-600' : 'opacity-90'}`}>
                              {dayjs(event.startTime).format('HH:mm')}
                            </div>

                            {shift && (
                              <div className="mt-1 flex items-center justify-between gap-1">
                                <div className="flex -space-x-1">
                                  {visibleAssignees.map((assignee) => (
                                    <ProfilePicture
                                      key={assignee.key}
                                      picture={assignee.picture}
                                      alias={getAssigneeAlias(assignee)}
                                      className="h-4 w-4 rounded-full border border-secondary-50 text-[8px]"
                                    />
                                  ))}
                                  {hiddenAssigneesCount > 0 && (
                                    <div className="flex h-4 w-4 items-center justify-center rounded-full border border-secondary-50 bg-secondary text-[8px] text-secondary-50">
                                      +{hiddenAssigneesCount}
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center gap-1 text-[10px] font-medium">
                                  <FaUsers className="h-2.5 w-2.5" />
                                  <span>
                                    {assignees.length}
                                    {event.maximumAttendees ? `/${event.maximumAttendees}` : ''}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {eventsForDay.length > (hasBirthdays ? 3 : 4) && (
                        <div className="text-xs text-gray-500 text-center py-1 rounded-[6px] bg-secondary-200 bg-opacity-50">
                          +{eventsForDay.length - (hasBirthdays ? 3 : 4)} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <ModalLayout
        closeButton
        open={shiftModalOpen}
        setOpen={(open) => {
          if (!open) {
            closeShiftModal();
          } else {
            setShiftModalOpen(true);
          }
        }}
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-6 sm:max-w-[620px]">
        {selectedShift && (
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold text-primary-900">{selectedShift.title}</h4>
                <p className="text-sm text-primary-500">
                  {dayjs(selectedShift.startTime).format('dddd D MMM YYYY, HH:mm')}
                  {selectedShift.endTime ? ` - ${dayjs(selectedShift.endTime).format('HH:mm')}` : ''}
                </p>
              </div>
            </div>

            {selectedShift.description && (
              <p className="text-sm ">{selectedShift.description}</p>
            )}

            <div className="flex items-center gap-2 text-sm ">
              <FaUsers className="h-3.5 w-3.5" />
              <span>
                {getShiftAssignees(selectedShift).length}
                {selectedShift.maximumAttendees ? ` / ${selectedShift.maximumAttendees}` : ''}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {getShiftAssignees(selectedShift).map((assignee) => (
                <div
                  key={assignee.key}
                  className="inline-flex items-center gap-2 rounded-full bg-secondary-200 bg-opacity-60 px-2 py-1 text-xs text-primary-900">
                  <ProfilePicture
                    picture={assignee.picture}
                    alias={getAssigneeAlias(assignee)}
                    className="h-6 w-6 rounded-full text-[10px]"
                  />
                  <span>{getAssigneeAlias(assignee)}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={closeShiftModal}>
                {t('general.close')}
              </Button>
              {selectedShift.scheduleId && (
                <Button
                  variant="primary"
                  onClick={() => {
                    navigate(`/events/schedule/${selectedShift.scheduleId}`);
                    closeShiftModal();
                  }}>
                  {t('page.event.schedules.title')}
                </Button>
              )}
            </div>
          </div>
        )}
      </ModalLayout>
    </div>
  );
}
